/**
 * Training Modules — the primary admin workspace.
 *
 * A "training module" is the complete compliance package the platform
 * assembles automatically when an SOP is uploaded: document, extracted
 * text, AI-generated learning module, question bank, linked competency
 * and staff assignment surface. Admins never need to touch documents,
 * competencies or question editing directly to get from "SOP uploaded"
 * to "staff assigned".
 *
 * Workflow: Upload SOP → Processing → Ready → Active → Archived.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Upload,
  Sparkles,
  Loader2,
  Users,
  FileText,
  BadgeCheck,
  Award,
  X,
  Archive,
  RotateCcw,
  Search,
  Percent,
  AlertTriangle,
  ChevronRight,
  CheckCircle2,
  Clock,
  BookOpen,
  Eye,
  BarChart3,
  Layers,
} from "lucide-react";
import AppShell from "../AppShell";
import { api, API, getToken } from "../api";
import { useAuth } from "../AuthContext";
import {
  PageHeader,
  Button,
  ErrorBanner,
  LoadingRow,
} from "../../components/primitives";

/* ------------------------------------------------------------------ */
/*                              helpers                               */
/* ------------------------------------------------------------------ */

const STATUS_META = {
  processing: {
    label: "Processing",
    dot: "bg-amber-400",
    pill: "bg-amber-50 text-amber-800 border-amber-200",
  },
  ready: {
    label: "Ready",
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-800 border-emerald-200",
  },
  active: {
    label: "Active",
    dot: "bg-[#D4A65A]",
    pill: "bg-[#FDF6E7] text-[#8C6B33] border-[#E9CE94]",
  },
  archived: {
    label: "Archived",
    dot: "bg-slate-400",
    pill: "bg-slate-100 text-slate-600 border-slate-200",
  },
  failed: {
    label: "Failed",
    dot: "bg-rose-500",
    pill: "bg-rose-50 text-rose-700 border-rose-200",
  },
};

const STAGE_LABELS = {
  uploading: "Uploading document",
  extracting: "Extracting document",
  generating: "AI generating training",
  creating_quiz: "Creating quiz",
  ready: "Ready to assign",
  failed: "Something went wrong",
};

const STAGE_ORDER = ["uploading", "extracting", "generating", "creating_quiz", "ready"];

function relativeDate(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.ready;
  return (
    <span
      data-testid={`tm-status-pill-${status}`}
      className={`inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] font-semibold border rounded-full px-2.5 py-0.5 ${meta.pill}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*                              PAGE                                  */
/* ------------------------------------------------------------------ */

export default function Learning() {
  const { business } = useAuth();
  const navigate = useNavigate();
  const { moduleId: urlModuleId } = useParams();

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [openModuleId, setOpenModuleId] = useState(urlModuleId || null);

  const isDemo = business?.is_demo;
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get("/training-modules");
      setModules(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll every 3 seconds while any module is still processing.
  useEffect(() => {
    const hasProcessing = modules.some((m) => m.status === "processing");
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (hasProcessing) {
      pollRef.current = setInterval(load, 3000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [modules, load]);

  // Sync open module → URL (deep-linkable side panel).
  useEffect(() => {
    if (openModuleId && urlModuleId !== openModuleId) {
      navigate(`/app/learning/${openModuleId}`, { replace: true });
    } else if (!openModuleId && urlModuleId) {
      navigate("/app/learning", { replace: true });
    }
  }, [openModuleId, urlModuleId, navigate]);

  const counts = useMemo(() => {
    const c = { all: modules.length, processing: 0, ready: 0, active: 0, archived: 0, failed: 0 };
    modules.forEach((m) => {
      c[m.status] = (c[m.status] || 0) + 1;
    });
    return c;
  }, [modules]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return modules.filter((m) => {
      if (statusFilter !== "all" && m.status !== statusFilter) return false;
      if (s) {
        const hay = `${m.title || ""} ${m.summary || ""} ${m.competency_name || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [modules, statusFilter, search]);

  const openModule = useMemo(
    () => modules.find((m) => m.id === openModuleId) || null,
    [modules, openModuleId]
  );

  return (
    <AppShell>
      <PageHeader
        testid="training-modules-page"
        kicker="Training modules"
        title="Every SOP, one complete training package"
        description="Upload an SOP and CanniComply builds the training module, quiz, certificate and compliance record automatically. Pick your staff and assign — no linking, no configuration."
        action={
          <Button
            variant="accent"
            icon={Upload}
            onClick={() => setShowUpload(true)}
            disabled={isDemo}
            data-testid="tm-upload-cta"
          >
            Upload SOP
          </Button>
        }
      />

      {isDemo && (
        <div className="mb-5 rounded-lg border border-[#D4A65A]/30 bg-[#FDF6E7] px-4 py-3 text-[13px] text-[#8C6B33]">
          Demo mode — uploads and assignments are disabled. Create a workspace
          to run the full workflow on your own SOPs.
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="tm-filter-bar">
        {[
          { key: "all", label: "All" },
          { key: "processing", label: "Processing" },
          { key: "ready", label: "Ready" },
          { key: "active", label: "Active" },
          { key: "archived", label: "Archived" },
          { key: "failed", label: "Failed" },
        ]
          .filter((f) => f.key === "all" || (counts[f.key] || 0) > 0)
          .map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`tm-filter-${f.key}`}
              className={`text-[12px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${
                statusFilter === f.key
                  ? "bg-[#0B1220] text-white border-[#0B1220]"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
            >
              {f.label} <span className="ml-1 text-[11px] opacity-70">({counts[f.key] || 0})</span>
            </button>
          ))}
        <div className="ml-auto relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="tm-search"
            placeholder="Search modules…"
            className="pl-8 pr-3 py-1.5 text-[12.5px] rounded-full border border-slate-200 focus:outline-none focus:border-slate-400 w-56"
          />
        </div>
      </div>

      {error && <ErrorBanner error={error} testid="tm-error" className="mb-5" />}

      {loading && <LoadingRow label="Loading training modules…" />}

      {!loading && modules.length === 0 && (
        <EmptyState onUpload={() => setShowUpload(true)} disabled={isDemo} />
      )}

      {!loading && modules.length > 0 && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500 text-sm">
          No modules match the current filter.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="tm-list">
        {filtered.map((m) => (
          <ModuleCard
            key={m.id}
            m={m}
            onOpen={() => setOpenModuleId(m.id)}
          />
        ))}
      </div>

      {showUpload && (
        <UploadDialog
          onClose={() => setShowUpload(false)}
          onCreated={(newModule) => {
            setModules((prev) => [newModule, ...prev.filter((m) => m.id !== newModule.id)]);
            setShowUpload(false);
            setOpenModuleId(newModule.id);
          }}
        />
      )}

      {openModuleId && openModule && (
        <SidePanel
          moduleId={openModuleId}
          onClose={() => setOpenModuleId(null)}
          onChanged={load}
          isDemo={isDemo}
        />
      )}
    </AppShell>
  );
}

/* ------------------------------------------------------------------ */
/*                          EMPTY STATE                               */
/* ------------------------------------------------------------------ */

function EmptyState({ onUpload, disabled }) {
  return (
    <div
      data-testid="tm-empty-state"
      className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center"
    >
      <span className="mx-auto h-12 w-12 rounded-lg bg-[#0B1220] text-[#D4A65A] flex items-center justify-center">
        <Sparkles size={20} />
      </span>
      <div className="mt-4 font-display text-lg text-slate-900 font-semibold">
        Upload your first SOP to build a training module
      </div>
      <p className="mt-2 text-slate-500 text-[13.5px] max-w-md mx-auto leading-relaxed">
        CanniComply will extract the document, generate a plain‑English
        learning module and a competency assessment, and hand you a card
        that is ready to assign to staff.
      </p>
      <button
        onClick={onUpload}
        disabled={disabled}
        data-testid="tm-empty-upload-btn"
        className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#D4A65A] hover:bg-[#E6BE7A] disabled:opacity-40 text-[#0B1220] px-4 py-2.5 text-[13.5px] font-semibold"
      >
        <Upload size={15} /> Upload SOP
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*                          UPLOAD DIALOG                             */
/* ------------------------------------------------------------------ */

function UploadDialog({ onClose, onCreated }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [passMark, setPassMark] = useState(80);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    // Client-side PDF validation before the FormData round-trip. Mirrors
    // backend 415 rule but gives the user instant feedback.
    const isPdf =
      f.type === "application/pdf" ||
      (f.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setError(
        "Only PDF documents are supported. Please convert your SOP to PDF and try again."
      );
      e.target.value = "";
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError("This file is over 10 MB. Please compress the PDF and try again.");
      e.target.value = "";
      return;
    }
    setError("");
    setFile(f);
    if (!title.trim()) {
      const base = (f.name || "").replace(/\.[^.]+$/, "");
      setTitle(base.replace(/[_-]+/g, " ").trim());
    }
  }

  async function submit() {
    setError("");
    if (!file) {
      setError("Choose a PDF file to upload.");
      return;
    }
    if (!title.trim()) {
      setError("Give the module a title.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("pass_mark", String(passMark));
      const res = await fetch(`${API}/training-modules/from-upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      onCreated(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-start md:items-center justify-center p-5 overflow-y-auto"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 my-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="tm-upload-dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">Upload SOP</h2>
            <p className="text-[13px] text-slate-500 mt-1">
              We'll extract, learn from and quiz-generate it for you.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">
              SOP or policy PDF
            </div>
            <label
              className="flex items-center gap-3 rounded-lg border border-dashed border-slate-300 px-4 py-3 cursor-pointer hover:border-slate-500"
              data-testid="tm-upload-file-label"
            >
              <Upload size={16} className="text-slate-500" />
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] text-slate-900 font-semibold truncate">
                  {file ? file.name : "Choose a PDF (up to 10 MB)"}
                </div>
                <div className="text-[11.5px] text-slate-500 truncate">
                  {file ? `${Math.round(file.size / 1024)} KB` : "PDF only"}
                </div>
              </div>
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={onPick}
                className="hidden"
                data-testid="tm-upload-file-input"
              />
            </label>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">
              Module title
            </div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Controlled Drug Handling SOP"
              data-testid="tm-upload-title"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-[13.5px] focus:outline-none focus:border-slate-400"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Pass mark</div>
              <div className="text-[12px] font-mono text-slate-700 font-semibold">{passMark}%</div>
            </div>
            <input
              type="range"
              min="50"
              max="100"
              step="5"
              value={passMark}
              onChange={(e) => setPassMark(Number(e.target.value))}
              data-testid="tm-upload-passmark"
              className="w-full accent-[#0B1220]"
            />
            <div className="mt-1 flex items-center justify-between text-[10.5px] text-slate-500">
              <span>50%</span>
              <span>Default 80% — recommended for compliance</span>
              <span>100%</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="text-[13px] text-slate-600 hover:text-slate-900 px-3 py-2"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="tm-upload-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-[#D4A65A] hover:bg-[#E6BE7A] disabled:opacity-40 text-[#0B1220] px-4 py-2.5 text-sm font-semibold"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Sparkles size={14} /> Build training module</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*                          MODULE CARD                               */
/* ------------------------------------------------------------------ */

function ProgressBar({ stage }) {
  const idx = STAGE_ORDER.indexOf(stage);
  const pct = idx < 0 ? 5 : Math.round(((idx + 1) / STAGE_ORDER.length) * 100);
  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 text-[11.5px] text-slate-600">
        <Loader2 size={12} className="animate-spin text-[#8C6B33]" />
        <span className="font-semibold">{STAGE_LABELS[stage] || "Working…"}</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#D4A65A] to-[#E6BE7A] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1.5 grid grid-cols-4 gap-1 text-[10px] uppercase tracking-[0.12em] text-slate-500">
        {STAGE_ORDER.slice(0, 4).map((s, i) => (
          <span key={s} className={i <= idx ? "text-[#8C6B33] font-semibold" : ""}>
            {STAGE_LABELS[s].replace("AI generating ", "AI ").replace("Uploading document", "Upload").replace("Extracting document", "Extract").replace("Creating quiz", "Quiz")}
          </span>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "default" }) {
  const toneCls =
    tone === "warn"
      ? "text-amber-800"
      : tone === "success"
      ? "text-emerald-700"
      : tone === "danger"
      ? "text-rose-700"
      : "text-slate-900";
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
        {label}
      </div>
      <div className={`mt-0.5 text-[15px] font-display font-semibold ${toneCls}`}>
        {value}
      </div>
    </div>
  );
}

function ModuleCard({ m, onOpen }) {
  const s = m.stats || {};
  const isProcessing = m.status === "processing";
  const isFailed = m.status === "failed";
  const summary = (m.summary || "").trim() || (isProcessing ? "AI is drafting a plain-English overview and question bank…" : "");
  const completionColor =
    s.completion_pct >= 90
      ? "success"
      : s.completion_pct >= 60
      ? "default"
      : s.assigned > 0
      ? "warn"
      : "default";

  return (
    <div
      data-testid={`tm-card-${m.id}`}
      className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#D4A65A]/60 hover:shadow-[0_2px_20px_-8px_rgba(11,18,32,0.15)] transition-all flex flex-col"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <span className="h-9 w-9 rounded-lg bg-[#D4A65A]/15 text-[#8C6B33] flex items-center justify-center shrink-0">
            <BookOpen size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusPill status={m.status} />
              {s.completion_pct === 100 && s.assigned > 0 && (
                <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 size={11} /> All complete
                </span>
              )}
              <span className="text-[10.5px] text-slate-400">
                Updated {relativeDate(m.updated_at || m.generated_at)}
              </span>
            </div>
            <button
              onClick={onOpen}
              data-testid={`tm-card-open-${m.id}`}
              className="mt-1 block font-display text-[16px] tracking-tight text-slate-900 font-semibold text-left hover:text-[#8C6B33] line-clamp-2"
            >
              {m.title || "Untitled module"}
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <p className="mt-2.5 text-[12.5px] text-slate-500 leading-relaxed line-clamp-2">
          {summary}
        </p>
      )}

      {/* Processing bar OR compliance metrics */}
      {isProcessing && <ProgressBar stage={m.progress_stage || "extracting"} />}
      {isFailed && (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700 flex items-start gap-2">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>{m.error || "Something went wrong while building this module."}</span>
        </div>
      )}

      {!isProcessing && !isFailed && (
        <>
          {/* Quiz + pass mark line */}
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <FileText size={11} /> {s.questions_total || 0} questions
            </span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Percent size={11} /> Pass mark {m.pass_mark || 80}%
            </span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Layers size={11} /> {(m.sections || []).length} sections
            </span>
          </div>

          {/* Completion health */}
          <div
            className="mt-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3 grid grid-cols-4 gap-3"
            data-testid={`tm-card-health-${m.id}`}
          >
            <Stat label="Assigned" value={s.assigned ?? 0} />
            <Stat label="In progress" value={s.in_progress ?? 0} />
            <Stat label="Completed" value={s.completed ?? 0} tone="success" />
            <Stat label="Overdue" value={s.overdue ?? 0} tone={s.overdue > 0 ? "danger" : "default"} />
            <Stat label="Completion" value={`${s.completion_pct ?? 0}%`} tone={completionColor} />
            <Stat label="Avg score" value={s.average_score != null ? `${s.average_score}%` : "—"} />
            <Stat label="Certificates" value={s.certificates_issued ?? 0} />
            <div>
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                Status
              </div>
              <div className="mt-0.5 text-[13px] font-semibold text-slate-800">
                {STATUS_META[m.status]?.label || "Ready"}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button
          onClick={onOpen}
          data-testid={`tm-card-preview-${m.id}`}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-md border border-slate-200 hover:border-slate-400 bg-white px-3 py-1.5 text-slate-700"
        >
          <Eye size={12} /> Preview
        </button>
        <button
          onClick={onOpen}
          disabled={isProcessing || m.status === "archived" || isFailed}
          data-testid={`tm-card-assign-${m.id}`}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-md bg-[#0B1220] hover:bg-[#1A2438] disabled:opacity-30 disabled:cursor-not-allowed text-white px-3 py-1.5"
        >
          <Users size={12} /> Assign staff
        </button>
        {s.assigned > 0 && (
          <button
            onClick={onOpen}
            data-testid={`tm-card-results-${m.id}`}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-md border border-slate-200 hover:border-slate-400 bg-white px-3 py-1.5 text-slate-700"
          >
            <BarChart3 size={12} /> Results
          </button>
        )}
        {(s.certificates_issued || 0) > 0 && (
          <button
            onClick={onOpen}
            data-testid={`tm-card-certificates-${m.id}`}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-md border border-slate-200 hover:border-slate-400 bg-white px-3 py-1.5 text-slate-700"
          >
            <Award size={12} /> Certificates
          </button>
        )}
        <div className="ml-auto">
          <button
            onClick={onOpen}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Open module"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*                            SIDE PANEL                              */
/* ------------------------------------------------------------------ */

function SidePanel({ moduleId, onClose, onChanged, isDemo }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [team, setTeam] = useState([]);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const [d, t] = await Promise.all([
        api.get(`/training-modules/${moduleId}`),
        api.get("/team").catch(() => []),
      ]);
      setDetail(d);
      setTeam(t || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    setLoading(true);
    reload();
  }, [reload]);

  // Poll while processing.
  useEffect(() => {
    const m = detail?.module;
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (m?.status === "processing") {
      pollRef.current = setInterval(reload, 3000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [detail?.module?.status, detail, reload]);

  async function archive() {
    if (isDemo) return;
    setBusy(true);
    try {
      await api.post(`/training-modules/${moduleId}/archive`);
      await reload();
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    if (isDemo) return;
    setBusy(true);
    try {
      await api.post(`/training-modules/${moduleId}/restore`);
      await reload();
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const module = detail?.module;
  const s = module?.stats || {};

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={() => !busy && onClose()}
    >
      <div
        className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="tm-side-panel"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {module ? (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill status={module.status} />
                  <span className="text-[11px] text-slate-500">
                    Updated {relativeDate(module.updated_at || module.generated_at)}
                  </span>
                </div>
                <h2 className="mt-1.5 font-display text-xl tracking-tight text-slate-900 font-semibold truncate">
                  {module.title || "Untitled module"}
                </h2>
              </>
            ) : (
              <h2 className="font-display text-xl text-slate-900 font-semibold">Training module</h2>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="tm-side-close"
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 px-6 py-5">
          {loading && (
            <div className="text-center text-slate-500 text-sm flex items-center justify-center gap-2 py-10">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          )}

          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700 mb-4">
              {error}
            </div>
          )}

          {module && (
            <>
              {module.status === "processing" && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-5">
                  <ProgressBar stage={module.progress_stage || "extracting"} />
                  <div className="mt-2 text-[12px] text-amber-800">
                    This usually takes around 30 seconds. You can close this
                    panel — the card will update automatically.
                  </div>
                </div>
              )}

              {module.status === "failed" && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-5 text-[13px] text-rose-700">
                  <div className="font-semibold mb-1 inline-flex items-center gap-1.5">
                    <AlertTriangle size={13} /> Build failed
                  </div>
                  {module.error || "Unknown error"}
                </div>
              )}

              {/* Tabs */}
              <div className="border-b border-slate-100 mb-4 flex items-center gap-4 text-[12.5px]">
                {[
                  { key: "overview", label: "Overview" },
                  { key: "assign", label: "Assign staff" },
                  { key: "results", label: `Results (${s.assigned || 0})` },
                  { key: "quiz", label: `Quiz (${s.questions_total || 0})` },
                  { key: "certs", label: `Certificates (${s.certificates_issued || 0})` },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`tm-side-tab-${t.key}`}
                    className={`pb-2 border-b-2 -mb-px font-semibold transition-colors ${
                      tab === t.key
                        ? "border-[#0B1220] text-[#0B1220]"
                        : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === "overview" && <OverviewTab detail={detail} />}
              {tab === "assign" && (
                <AssignTab
                  moduleId={moduleId}
                  team={team}
                  detail={detail}
                  disabled={isDemo || module.status === "processing" || module.status === "archived" || module.status === "failed"}
                  onDone={async () => {
                    await reload();
                    onChanged();
                  }}
                />
              )}
              {tab === "results" && <ResultsTab detail={detail} />}
              {tab === "quiz" && <QuizTab detail={detail} />}
              {tab === "certs" && <CertsTab detail={detail} />}
            </>
          )}
        </div>

        {/* Footer actions */}
        {module && (
          <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-3 flex items-center justify-between">
            <div className="text-[11px] text-slate-500">
              Module ID <span className="font-mono text-slate-700">{module.id.slice(0, 8)}</span>
            </div>
            <div className="flex items-center gap-2">
              {module.status !== "archived" ? (
                <button
                  onClick={archive}
                  disabled={busy || isDemo}
                  data-testid="tm-side-archive"
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-md border border-slate-200 hover:border-slate-400 bg-white px-3 py-1.5 text-slate-700 disabled:opacity-40"
                >
                  <Archive size={12} /> Archive
                </button>
              ) : (
                <button
                  onClick={restore}
                  disabled={busy || isDemo}
                  data-testid="tm-side-restore"
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-md border border-slate-200 hover:border-slate-400 bg-white px-3 py-1.5 text-slate-700 disabled:opacity-40"
                >
                  <RotateCcw size={12} /> Restore
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------- Tabs ------------- */

function OverviewTab({ detail }) {
  const m = detail.module;
  const doc = detail.document;
  const s = m.stats || {};
  const stageRows = [
    ["Assigned", s.assigned ?? 0],
    ["In progress", s.in_progress ?? 0],
    ["Completed", s.completed ?? 0],
    ["Overdue", s.overdue ?? 0],
    ["Completion", `${s.completion_pct ?? 0}%`],
    ["Average score", s.average_score != null ? `${s.average_score}%` : "—"],
    ["Certificates issued", s.certificates_issued ?? 0],
    ["Pass mark", `${m.pass_mark || 80}%`],
  ];
  return (
    <div className="space-y-5" data-testid="tm-overview-tab">
      {m.summary && (
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">
            AI summary
          </div>
          <p className="text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-wrap">
            {m.summary}
          </p>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        {stageRows.map(([label, value]) => (
          <div key={label}>
            <div className="text-[9.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
              {label}
            </div>
            <div className="mt-0.5 text-[15px] font-display font-semibold text-slate-900">
              {value}
            </div>
          </div>
        ))}
      </div>

      {(m.sections || []).length > 0 && (
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">
            Learning sections
          </div>
          <div className="space-y-2.5">
            {m.sections.map((sec, i) => (
              <details key={i} className="rounded-lg border border-slate-200 bg-white group">
                <summary className="cursor-pointer list-none px-4 py-2.5 flex items-center gap-2 text-[13.5px] font-semibold text-slate-900">
                  <ChevronRight size={14} className="transition-transform group-open:rotate-90" />
                  {sec.heading || `Section ${i + 1}`}
                </summary>
                <div className="px-4 pb-3 text-[13px] text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {sec.body}
                  {Array.isArray(sec.key_takeaways) && sec.key_takeaways.length > 0 && (
                    <ul className="mt-2 space-y-1 text-[12.5px]">
                      {sec.key_takeaways.map((k, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D4A65A] shrink-0" />
                          <span>{k}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {doc && (
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">
            Source SOP
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3">
            <FileText size={16} className="text-slate-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-slate-900 truncate">
                {doc.title} <span className="text-slate-400 font-normal">v{doc.version}</span>
              </div>
              <div className="text-[11.5px] text-slate-500 truncate">
                {doc.original_filename}
              </div>
            </div>
            <a
              href={`${API}/documents/${doc.id}/download`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={async (e) => {
                e.preventDefault();
                const res = await fetch(`${API}/documents/${doc.id}/download`, {
                  headers: { Authorization: `Bearer ${getToken()}` },
                });
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank");
              }}
              className="text-[12px] font-semibold text-[#8C6B33] hover:underline"
            >
              Open PDF
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function AssignTab({ moduleId, team, detail, disabled, onDone }) {
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const alreadyAssignedIds = useMemo(
    () => new Set((detail.assignments || []).filter((a) => a.status !== "completed").map((a) => a.team_member_id)),
    [detail]
  );

  const ids = Object.keys(selected).filter((id) => selected[id]);

  async function submit() {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const r = await api.post(`/training-modules/${moduleId}/assign`, {
        team_member_ids: ids,
      });
      setResult(r);
      setSelected({});
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="tm-assign-tab">
      {team.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-slate-500 text-sm">
          Add team members first from the <b>Team</b> page.
        </div>
      )}

      {team.length > 0 && (
        <>
          <div className="text-[12px] text-slate-500 mb-2">
            Select the employees who need to complete this training. Everyone
            selected will get an email invite and see it on their My Training
            page.
          </div>
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {team.map((m) => {
              const already = alreadyAssignedIds.has(m.id);
              return (
                <label
                  key={m.id}
                  data-testid={`tm-assign-tm-${m.id}`}
                  className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 ${
                    already ? "opacity-70" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!selected[m.id] || already}
                    disabled={already}
                    onChange={(e) => setSelected({ ...selected, [m.id]: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-slate-900 truncate">
                      {m.name}
                    </div>
                    <div className="text-[11.5px] text-slate-500 truncate">
                      {m.role}
                      {m.department ? ` · ${m.department}` : ""}
                    </div>
                  </div>
                  {already && (
                    <span className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-[0.14em]">
                      Already assigned
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          {error && (
            <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
              {error}
            </div>
          )}
          {result && (
            <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800 flex items-center gap-2">
              <CheckCircle2 size={14} /> Assigned to {result.assigned}{" "}
              {result.assigned === 1 ? "person" : "people"}.
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <div className="text-[11.5px] text-slate-500">
              Selected: <b className="text-slate-900">{ids.length}</b>
            </div>
            <button
              onClick={submit}
              disabled={busy || disabled || ids.length === 0}
              data-testid="tm-assign-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-[#D4A65A] hover:bg-[#E6BE7A] disabled:opacity-40 text-[#0B1220] px-4 py-2 text-[13px] font-semibold"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />}
              {busy ? "Assigning…" : `Assign training`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ResultsTab({ detail }) {
  const rows = detail.assignments || [];
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-slate-500 text-sm">
        Nobody assigned yet.
      </div>
    );
  }
  const statusPill = (a) => {
    if (a.status === "completed") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (a.status === "in_progress") return "bg-amber-50 text-amber-800 border-amber-200";
    return "bg-slate-100 text-slate-600 border-slate-200";
  };
  return (
    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="tm-results-tab">
      {rows.map((a) => (
        <div key={a.id} className="flex items-center justify-between px-4 py-2.5 gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-semibold text-slate-900 truncate">
              {a.team_member_name}
            </div>
            <div className="text-[11.5px] text-slate-500 truncate">
              {a.team_member_role || "—"} · Assigned {relativeDate(a.assigned_at)}
            </div>
          </div>
          {a.score != null && (
            <span className="text-[12px] text-slate-600 font-mono">{a.score}%</span>
          )}
          <span
            className={`text-[10.5px] uppercase tracking-[0.14em] font-semibold border rounded-full px-2 py-0.5 ${statusPill(a)}`}
          >
            {a.status.replace("_", " ")}
          </span>
        </div>
      ))}
    </div>
  );
}

function QuizTab({ detail }) {
  const qs = detail.questions || [];
  if (qs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-slate-500 text-sm">
        The quiz is being generated…
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="tm-quiz-tab">
      {qs.map((q, i) => (
        <div key={q.id} className="rounded-lg border border-slate-200 p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-mono font-semibold">
              Q.{String(i + 1).padStart(2, "0")}
            </span>
            <span className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400">
              {q.source_section || "SOP"}
            </span>
          </div>
          <div className="text-[13.5px] text-slate-900 font-semibold">{q.prompt}</div>
          <ul className="mt-2 space-y-1 text-[12.5px] text-slate-700">
            {(q.options || []).map((opt) => (
              <li
                key={opt.key || opt.label}
                className={`flex items-start gap-2 ${
                  q.correct_key && opt.key === q.correct_key ? "text-emerald-700 font-semibold" : ""
                }`}
              >
                <span className="w-4 text-[11px] font-mono text-slate-400">{opt.key || "-"}</span>
                <span>{opt.text || opt.label || ""}</span>
              </li>
            ))}
          </ul>
          {q.explanation && (
            <div className="mt-2 text-[11.5px] text-slate-500 italic">
              Why: {q.explanation}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CertsTab({ detail }) {
  const certs = detail.certificates || [];
  if (certs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-slate-500 text-sm">
        Certificates will appear here once staff complete the assessment.
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="tm-certs-tab">
      {certs.map((c) => (
        <div key={c.cert_id || c.id} className="flex items-center justify-between px-4 py-2.5 gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-semibold text-slate-900 truncate">
              {c.team_member_name}
            </div>
            <div className="text-[11.5px] text-slate-500 truncate font-mono">
              {c.cert_id} · {relativeDate(c.completed_at)}
            </div>
          </div>
          <BadgeCheck size={16} className="text-[#8C6B33]" />
        </div>
      ))}
    </div>
  );
}
